<template>
  <div
    class="contact-dialog"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('Contact Us')"
    @keydown.esc="closeModal"
  >
    <div class="contact-dialog__overlay" @click="closeModal"></div>

    <div class="contact-dialog__glow"></div>

    <div class="contact-dialog__viewport">
      <section class="contact-dialog__card">
        <button
          type="button"
          class="contact-dialog__close"
          :aria-label="$t('Close')"
          @click="closeModal"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M6 6L18 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="square"
            />
            <path
              d="M18 6L6 18"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="square"
            />
          </svg>
        </button>

        <header class="contact-dialog__header">
          <div class="contact-dialog__eyebrow _subtitle">
            {{ $t('СВЯЗАТЬСЯ С НАМИ') }}
          </div>

          <h2 class="contact-dialog__title">
            {{ $t('Заполните форму') }}
          </h2>

          <p class="contact-dialog__description">
            {{
              $t(
                'Оставьте сообщение, и наша команда свяжется с вами как можно скорее.',
              )
            }}
          </p>
        </header>

        <form class="contact-dialog__form" @submit.prevent="submitContact">
          <label class="contact-field">
            <span class="contact-field__label">
              {{ $t('Полное имя') }}
            </span>

            <input
              v-model="name"
              type="text"
              class="contact-field__input"
              :placeholder="$t('Полное имя')"
              autocomplete="name"
              maxlength="80"
              @input="filterName"
            />
          </label>

          <label class="contact-field">
            <span class="contact-field__label">
              {{ $t('E-mail') }}
            </span>

            <input
              v-model.trim="email"
              type="email"
              class="contact-field__input"
              :placeholder="$t('Email')"
              autocomplete="email"
              maxlength="120"
            />
          </label>

          <label class="contact-field">
            <span class="contact-field__label">
              {{ $t('Сообщение') }}
            </span>

            <textarea
              v-model="message"
              class="contact-field__input contact-field__input_textarea"
              :placeholder="$t('Сообщение')"
              maxlength="1000"
              @input="filterMessage"
            ></textarea>

            <span class="contact-field__counter">
              {{ message.length }}/1000
            </span>
          </label>

          <div class="contact-checkbox">
            <label class="contact-checkbox__control">
              <input v-model="termsAccepted" type="checkbox" name="terms" />

              <span class="contact-checkbox__box">
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M3 8.5L6.3 11.5L13 4.5"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </label>

            <span class="contact-checkbox__text">
              {{ $t('Я согласен с') }}

              <button
                v-if="termsPage"
                type="button"
                class="contact-checkbox__link"
                @click.stop="openLegalPage(termsPage)"
              >
                {{ termsPage.title }}
              </button>

              <template v-if="termsPage && privacyPage">
                {{ $t('и') }}
              </template>

              <button
                v-if="privacyPage"
                type="button"
                class="contact-checkbox__link"
                @click.stop="openLegalPage(privacyPage)"
              >
                {{ privacyPage.title }}
              </button>
            </span>
          </div>

          <Captcha v-model="captcha" />

          <button
            type="submit"
            class="button shadowed contact-dialog__submit"
            :disabled="!formIsValid || submitting || !!successMessage"
          >
            <span v-if="successMessage">
              {{ $t('ОТПРАВЛЕНО') }}
            </span>

            <span v-else-if="!submitting">
              {{ $t('ОТПРАВИТЬ') }}
            </span>

            <span
              v-else
              class="contact-dialog__loader"
              aria-hidden="true"
            ></span>
          </button>
        </form>

        <transition name="contact-message">
          <div
            v-if="successMessage"
            class="contact-dialog__message contact-dialog__message_success"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="square"
                stroke-linejoin="round"
              />
            </svg>

            <span>{{ successMessage }}</span>
          </div>
        </transition>

        <transition name="contact-message">
          <div
            v-if="$parent.error"
            class="contact-dialog__message contact-dialog__message_error"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M12 8V13"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
              <path
                d="M12 17H12.01"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
              <path
                d="M10.3 3.6L2.4 17.2C1.6 18.6 2.6 20.3 4.2 20.3H19.8C21.4 20.3 22.4 18.6 21.6 17.2L13.7 3.6C12.9 2.2 11.1 2.2 10.3 3.6Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linejoin="round"
              />
            </svg>

            <span>{{ $parent.error }}</span>
          </div>
        </transition>

        <p class="contact-dialog__footer">
          {{
            $t(
              'Отправляя форму, вы соглашаетесь с правилами обработки обращений.',
            )
          }}
        </p>
      </section>
    </div>
  </div>
</template>

<script>
import Captcha from './Captcha.vue'

export default {
  name: 'ContactUs',

  components: { Captcha },

  emits: ['sendContactForm'],

  props: {
    textPageList: {
      type: Array,
      default: () => [],
    },

    successMessage: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      email: '',
      name: '',
      message: '',
      captcha: '',
      submitting: false,
      termsAccepted: false,
      closeTimer: null,
    }
  },

  computed: {
    termsPage() {
      return (
        this.textPageList.find((item) => {
          return Number(item?.is_terms) === 1
        }) || null
      )
    },

    privacyPage() {
      return (
        this.textPageList.find((item) => {
          return Number(item?.is_privacy) === 1
        }) || null
      )
    },

    errorMessage() {
      return this.$parent?.error || ''
    },

    formIsValid() {
      return (
        this.name.trim().length >= 2 &&
        this.isValidEmail(this.email) &&
        this.message.trim().length >= 5 &&
        this.termsAccepted &&
        !!this.captcha
      )
    },
  },

  watch: {
    successMessage(value) {
      if (!value) {
        return
      }

      this.submitting = true

      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
      }

      this.closeTimer = setTimeout(() => {
        this.closeModal()
      }, 2000)
    },

    errorMessage(value) {
      if (!value) {
        return
      }

      if (this.successMessage) {
        return
      }

      this.submitting = false
    },
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)

    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
  },

  methods: {
    openLegalPage(page) {
      if (!page) {
        return
      }

      this.closeModal()

      if (this.$parent && typeof this.$parent.goToPage === 'function') {
        this.$parent.goToPage(page)
      }
    },

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closeModal()
      }
    },

    closeModal() {
      if (this.$parent && typeof this.$parent.closeContactUs === 'function') {
        this.$parent.closeContactUs()
      }
    },

    filterName(event) {
      let value = event.target.value

      value = value
        .replace(/[^a-zA-Zа-яА-ЯёЁ\s'-]/g, '')
        .replace(/\s+/g, ' ')
        .trimStart()

      this.name = value
    },

    filterMessage(event) {
      let value = event.target.value

      value = value.replace(/\r\n/g, '\n').replace(/[^\S\n]+/g, ' ')

      this.message = value.slice(0, 2000)
    },

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
    },

    submitContact() {
      if (!this.formIsValid || this.submitting || this.successMessage) {
        return
      }

      this.submitting = true

      if (this.$parent && typeof this.$parent.clearError === 'function') {
        this.$parent.clearError()
      }

      this.$emit('sendContactForm', {
        name: this.name.trim(),
        email: this.email.trim(),
        message: this.message.trim(),
        altcha: this.captcha,
      })
    },
  },
}
</script>

<style scoped lang="scss">
.contact-dialog {
  position: fixed;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
}

.contact-dialog__overlay {
  position: fixed;
  inset: 0;

  background: radial-gradient(
      circle at 50% -10%,
      rgba(33, 193, 126, 0.08),
      transparent 42%
    ),
    rgba(5, 8, 7, 0.88);

  backdrop-filter: blur(8px);
  cursor: pointer;
}

.contact-dialog__glow {
  position: fixed;
  top: -320px;
  left: 50%;
  width: 860px;
  height: 560px;

  transform: translateX(-50%);

  background: radial-gradient(
    circle,
    rgba(33, 193, 126, 0.28) 0%,
    rgba(33, 193, 126, 0.08) 38%,
    transparent 72%
  );

  filter: blur(18px);
  pointer-events: none;
}

.contact-dialog__viewport {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 520px;
  margin: auto;
}

.contact-dialog__card {
  position: relative;
  width: 100%;
  padding: 38px;

  background: linear-gradient(160deg, rgba(33, 193, 126, 0.04), transparent 48%),
    var(--bunker-86);

  border: 1px solid var(--outer-space);
  border-radius: 12px;

  box-shadow: 0 42px 100px rgba(0, 0, 0, 0.58),
    0 0 60px rgba(33, 193, 126, 0.045),
    inset 2px 2px 0 rgba(255, 255, 255, 0.02),
    inset -2px -2px 0 rgba(0, 0, 0, 0.2);
}

.contact-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;

  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  color: var(--pewter);
  background: var(--heavy-metal);
  border: 1px solid var(--outer-space);
  border-radius: 8px;

  transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease, transform 0.2s ease;

  svg {
    width: 15px;
    height: 15px;
  }

  @media (any-hover: hover) {
    &:hover {
      color: var(--gray-nurse);
      background: var(--outer-space);
      border-color: rgba(33, 193, 126, 0.35);
      transform: rotate(90deg);
    }
  }
}

.contact-dialog__header {
  padding-right: 38px;
  margin-bottom: 28px;
}

.contact-dialog__eyebrow {
  margin-bottom: 12px;
  text-transform: uppercase;
}

.contact-dialog__title {
  margin: 0;

  font-family: var(--font-2);
  font-size: 28px;
  font-weight: 700;
  line-height: 36px;

  color: var(--gray-nurse);
}

.contact-dialog__description {
  max-width: 390px;
  margin: 8px 0 0;

  font-family: var(--font-1);
  font-size: 13px;
  font-weight: 400;
  line-height: 21px;

  color: var(--mantle);
}

.contact-dialog__form {
  display: flex;
  flex-direction: column;
  gap: 17px;
}

.contact-field {
  position: relative;
  display: block;
  margin: 0;
  padding: 0;
}

.contact-checkbox {
  display: flex;
  align-items: flex-start;

  gap: 11px;

  margin: 2px 0 0;
}

.contact-checkbox__control {
  position: relative;

  flex: 0 0 21px;

  width: 21px;
  height: 21px;

  margin: 0;

  cursor: pointer;

  > input {
    position: absolute;

    width: 1px;
    height: 1px;

    opacity: 0;

    pointer-events: none;
  }

  > input:checked + .contact-checkbox__box {
    color: var(--racing-green);

    background: linear-gradient(142deg, var(--44), var(--31));

    border-color: var(--31);

    svg {
      opacity: 1;
      transform: scale(1);
    }
  }

  > input:focus-visible + .contact-checkbox__box {
    box-shadow: 0 0 0 3px rgba(33, 193, 126, 0.12);
  }
}

.contact-checkbox__box {
  width: 21px;
  min-width: 21px;
  height: 21px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: transparent;

  background: rgba(8, 14, 11, 0.72);

  border: 1px solid var(--outer-space);
  border-radius: 5px;

  cursor: pointer;

  transition: border-color 0.2s ease, background-color 0.2s ease,
    box-shadow 0.2s ease;

  svg {
    width: 13px;
    height: 13px;

    opacity: 0;
    transform: scale(0.7);

    transition: opacity 0.2s ease, transform 0.2s ease;
  }
}

.contact-checkbox__text {
  flex: 1 1 auto;

  font-family: var(--font-1);
  font-size: 11px;
  line-height: 18px;

  color: var(--mantle);
}

.contact-checkbox__link {
  display: inline;

  margin: 0;
  padding: 0;

  font: inherit;
  font-weight: 500;
  line-height: inherit;

  color: var(--mountain-meadow);

  background: transparent;
  border: 0;

  cursor: pointer;

  transition: color 0.2s ease, text-decoration-color 0.2s ease;

  @media (any-hover: hover) {
    &:hover {
      color: var(--gray-nurse);
      text-decoration: underline;
    }
  }
}

.contact-field__label {
  display: block;
  margin-bottom: 7px;

  font-family: var(--font-1);
  font-size: 12px;
  font-weight: 600;
  line-height: 17px;

  color: var(--pewter);
}

.contact-field__input {
  display: block;
  width: 100%;
  min-height: 46px;
  margin: 0;
  padding: 11px 14px;

  font-family: var(--font-1);
  font-size: 13px;
  font-weight: 400;
  line-height: 21px;

  color: var(--gray-nurse);
  background: rgba(8, 14, 11, 0.72);
  border: 1px solid var(--outer-space);
  border-radius: 9px;
  outline: none;
  appearance: none;

  box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.34),
    inset -1px -1px 0 rgba(255, 255, 255, 0.025);

  transition: border-color 0.2s ease, background-color 0.2s ease,
    box-shadow 0.2s ease;

  &::placeholder {
    font-family: var(--font-1);
    font-size: 13px;
    color: var(--mantle);
    opacity: 0.65;
  }

  &:focus {
    background: var(--heavy-metal);
    border-color: var(--31);

    box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.3),
      0 0 0 3px rgba(33, 193, 126, 0.1);
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: var(--gray-nurse);
    transition: background-color 9999s ease-in-out 0s;
  }
}

.contact-field__input_textarea {
  min-height: 132px;
  padding-bottom: 30px;
  resize: none;
}

.contact-field__counter {
  position: absolute;
  right: 12px;
  bottom: 10px;

  font-family: var(--font-1);
  font-size: 10px;
  line-height: 14px;

  color: var(--mantle);
  pointer-events: none;
}

.contact-dialog__submit {
  width: 100%;
  min-height: 48px;
  justify-content: center;
  margin-top: 4px;
  padding: 13px 18px;

  font-family: var(--font-3);
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  text-transform: uppercase;
}

.contact-dialog__loader {
  width: 21px;
  height: 21px;
  display: block;

  border: 3px solid rgba(7, 16, 12, 0.3);
  border-top-color: var(--racing-green);
  border-radius: 50%;

  animation: contact-dialog-loader 0.75s linear infinite;
}

.contact-dialog__message {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 17px;
  padding: 11px 13px;

  font-family: var(--font-1);
  font-size: 12px;
  line-height: 18px;

  border-radius: 8px;

  svg {
    flex: 0 0 17px;
    width: 17px;
    height: 17px;
    margin-top: 1px;
  }

  &_success {
    color: var(--mountain-meadow);
    background: var(--mountain-meadow-10);
    border: 1px solid var(--mountain-meadow-25);
  }

  &_error {
    color: #ff8b9e;
    background: rgba(255, 71, 102, 0.08);
    border: 1px solid rgba(255, 71, 102, 0.25);
  }
}

.contact-dialog__footer {
  max-width: 360px;
  margin: 20px auto 0;

  font-family: var(--font-1);
  font-size: 10px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;

  color: var(--mantle);
  opacity: 0.72;
}

.contact-message-enter-active,
.contact-message-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.contact-message-enter,
.contact-message-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@keyframes contact-dialog-loader {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 767.98px) {
  .contact-dialog {
    align-items: flex-start;
    padding: 18px 16px;
  }

  .contact-dialog__viewport {
    max-width: 480px;
    padding: 20px 0;
  }

  .contact-dialog__card {
    padding: 28px 22px;
  }

  .contact-dialog__header {
    margin-bottom: 23px;
  }

  .contact-dialog__title {
    font-size: 24px;
    line-height: 31px;
  }

  .contact-dialog__description {
    font-size: 12px;
    line-height: 19px;
  }
}

@media (max-width: 480px) {
  .contact-dialog {
    padding: 0;
  }

  .contact-dialog__viewport {
    min-height: 100%;
    max-width: none;
    padding: 12px;
    display: flex;
    align-items: center;
  }

  .contact-dialog__card {
    padding: 26px 18px 22px;
    border-radius: 10px;
  }

  .contact-dialog__close {
    top: 12px;
    right: 12px;
  }

  .contact-dialog__header {
    padding-right: 34px;
  }

  .contact-dialog__form {
    gap: 15px;
  }

  .contact-field__input {
    min-height: 44px;
  }

  .contact-field__input_textarea {
    min-height: 115px;
  }
}
</style>
